import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserContext } from "../shared/UserContext";
import {
  collection, collectionGroup, query, where, getDocs, onSnapshot, limit,
} from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useTour } from "../shared/GuidedTour";
import useFetchClasses from "../hooks/useTeacherClasses";
import { isSuperAdmin } from "../shared/superAdmin";
import Invitations from "./Invitations";
import WelcomeModal from "./WelcomeModal";
import "./Dashboard.css";

const STORAGE_PREFIX = "classboon_onboarding_done";
const WELCOME_PREFIX = "classboon_welcome_seen";

// Local-date key (matches Attendance's todayKeyLocal) for reading today's attendance.
function todayKeyLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const Dashboard = () => {
  const { user, role, uid, schoolId, libraryEnabled, lendingTrackers, lunchEnabled, features } = useContext(UserContext);
  const { startTour } = useTour();
  const location = useLocation();
  const { classList } = useFetchClasses();
  const isAdmin = role === "admin";
  const isElevated = role === "admin" || role === "security";
  const isSuper = isSuperAdmin({ uid, email: user?.email });
  const hasLending = lendingTrackers.length > 0;

  // Scope the dismissal per user — otherwise dismissing onboarding on one account
  // hides it for every other account that logs in on the same browser.
  const storageKey = user?.uid ? `${STORAGE_PREFIX}_${user.uid}` : null;
  const welcomeKey = user?.uid ? `${WELCOME_PREFIX}_${user.uid}` : null;
  const [steps, setSteps] = useState({ hasClasses: false, hasStudents: false, hasVisitedSignOut: false });
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  // Live "at a glance" badges (all reuse existing rule-authorized queries).
  const [outNow, setOutNow] = useState(0);       // students this teacher has signed out right now
  const [lendingOut, setLendingOut] = useState(0); // items currently checked out via Lending
  const [attend, setAttend] = useState(null);      // { present, total } for the homeroom, today

  useEffect(() => {
    if (storageKey) setDismissed(localStorage.getItem(storageKey) === "true");
  }, [storageKey]);

  useEffect(() => {
    if (!user?.uid) return;
    const checkProgress = async () => {
      const snap = await getDocs(
        query(collection(firestore, "classes"), where("teacherId", "==", user.uid))
      );
      const activeClasses = snap.docs.filter(d => !d.data().isArchived);

      // First-run welcome: only for accounts that have the seeded sample class, once per user.
      const hasSample = snap.docs.some(d => d.data().isSample === true);
      if (hasSample && welcomeKey && localStorage.getItem(welcomeKey) !== "true") setShowWelcome(true);

      const hasClasses = activeClasses.length > 0;
      const studentSnap = await getDocs(
        query(collection(firestore, "students"), where("addedBy", "==", user.uid), limit(1))
      );
      const hasStudents = !studentSnap.empty;
      const hasVisitedSignOut =
        localStorage.getItem(`classboon_onboarding_signout_${user.uid}`) === "true";

      setSteps({ hasClasses, hasStudents, hasVisitedSignOut });
      setLoading(false);
    };
    checkProgress();
  }, [user?.uid, welcomeKey]);

  // Sign-Out badge: how many students this teacher has out right now.
  useEffect(() => {
    if (!uid) return undefined;
    return onSnapshot(
      query(collection(firestore, "signouts"), where("teacherUID", "==", uid)),
      snap => setOutNow(snap.size),
      err => console.warn("out-now badge:", err)
    );
  }, [uid]);

  // Lending badge: items currently out (time_in still null) across this teacher's loans.
  useEffect(() => {
    if (!uid || !hasLending) { setLendingOut(0); return undefined; }
    return onSnapshot(
      query(collectionGroup(firestore, "lendingRecords"), where("teacherUID", "==", uid)),
      snap => setLendingOut(snap.docs.filter(d => d.data().time_in === null).length),
      err => console.warn("lending badge:", err)
    );
  }, [uid, hasLending]);

  // Attendance badge: present / total for the teacher's homeroom today. Total roster
  // is a one-time count; present is live off the attendance subcollection.
  const homeroom = classList.find(c => c.isHomeroom) || classList.find(c => !c.isArchived);
  const homeroomId = homeroom?.id;
  useEffect(() => {
    if (!homeroomId || !schoolId) { setAttend(null); return undefined; }
    let total = 0;
    getDocs(query(
      collection(firestore, "students"),
      where("schoolId", "==", schoolId),
      where("enrolledClasses", "array-contains", homeroomId)
    )).then(s => { total = s.size; }).catch(() => {});
    return onSnapshot(
      collection(firestore, "classes", homeroomId, "attendance", todayKeyLocal(), "students"),
      snap => setAttend({ present: snap.size, total }),
      err => console.warn("attendance badge:", err)
    );
  }, [homeroomId, schoolId]);

  // The onboarding wizard can hand off straight into the guided tour via nav state.
  const tourStartedRef = useRef(false);
  useEffect(() => {
    if (location.state?.startTour && !tourStartedRef.current) {
      tourStartedRef.current = true;
      startTour();
    }
  }, [location.state, startTour]);

  const allDone = steps.hasClasses && steps.hasStudents && steps.hasVisitedSignOut;

  const handleDismiss = () => {
    if (storageKey) localStorage.setItem(storageKey, "true");
    setDismissed(true);
  };
  const closeWelcome = () => {
    if (welcomeKey) localStorage.setItem(welcomeKey, "true");
    setShowWelcome(false);
  };
  const handleStartTour = () => { closeWelcome(); startTour(); };

  useEffect(() => {
    if (allDone && !dismissed) {
      const t = setTimeout(handleDismiss, 3000);
      return () => clearTimeout(t);
    }
  }, [allDone, dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  const greeting = user?.displayName || "there";
  const showChecklist = !loading && !dismissed && !allDone;

  // Feature-aware tile groups. Badges are omitted (undefined) until they have a value.
  const dailyTiles = [
    features.signOut && { to: "/classsignoutpage", icon: "🚪", label: "Sign-Out", badge: outNow > 0 ? `${outNow} out` : null, tone: "hot" },
    features.attendance && { to: "/attendance", icon: "✅", label: "Attendance", badge: attend?.total ? `${attend.present}/${attend.total}` : null },
    features.checklist && { to: "/emergency", icon: "📋", label: "Check List" },
    libraryEnabled && { to: "/library", icon: "📚", label: "Library" },
    hasLending && { to: "/lending", icon: "🔧", label: "Lending", badge: lendingOut > 0 ? `${lendingOut} out` : null, tone: "hot" },
    lunchEnabled && { to: "/attendance", state: { mode: "lunch" }, icon: "🍽", label: "Lunch Count" },
  ].filter(Boolean);

  const manageTiles = [
    { to: "/displayclasses", icon: "👥", label: "Classes" },
    { to: "/displayenrolledstudents", icon: "🎓", label: "Students" },
    { to: "/studentinsights", icon: "📊", label: "Insights" },
    { to: "/signoutsteacherrecord", icon: "🕘", label: "Sign-Out History" },
    { to: "/attendanceviewer", icon: "📅", label: "Attendance History" },
    { to: "/settings", icon: "⚙️", label: "Settings" },
  ];

  const adminTiles = [
    isElevated && { to: "/schoolview", icon: "🏫", label: "School View" },
    isAdmin && { to: "/schoolroster", icon: "🗂", label: "School Roster" },
    isAdmin && { to: "/schooladmin", icon: "🧑‍💼", label: "Manage Users" },
    isSuper && { to: "/adminpanel", icon: "🛡", label: "Super Admin" },
  ].filter(Boolean);

  return (
    <div className="dashboard">
      {showWelcome && (
        <WelcomeModal
          teacherName={user?.displayName}
          onExplore={closeWelcome}
          onStartTour={handleStartTour}
        />
      )}
      <div className="dashboard-content">
        <h1 style={{ marginBottom: 4 }}>Welcome back, {greeting}! 👋</h1>
        <p className="home-sub">Jump to anything below.</p>

        {/* Pending invitations surface at the top, and self-hide when there are none */}
        <Invitations user={user} hideWhenEmpty />

        {showChecklist && (
          <Checklist steps={steps} allDone={allDone} onDismiss={handleDismiss} />
        )}

        <TileSection title="Daily" tiles={dailyTiles} />
        <TileSection title="Manage" tiles={manageTiles} />
        {adminTiles.length > 0 && <TileSection title="Admin" tiles={adminTiles} />}
      </div>
    </div>
  );
};

const TileSection = ({ title, tiles }) => (
  <section className="home-section">
    <h2 className="home-section-title">{title}</h2>
    <div className="home-grid">
      {tiles.map(t => (
        <Link key={t.label} to={t.to} state={t.state} className="home-tile">
          {t.badge && <span className={`home-tile-badge${t.tone === "hot" ? " is-hot" : ""}`}>{t.badge}</span>}
          <span className="home-tile-icon" aria-hidden="true">{t.icon}</span>
          <span className="home-tile-label">{t.label}</span>
        </Link>
      ))}
    </div>
  </section>
);

const Checklist = ({ steps, allDone, onDismiss }) => (
  <div className="card" style={{ maxWidth: 520, marginBottom: "var(--space-6)", position: "relative" }}>
    <button
      onClick={onDismiss}
      style={{
        position: "absolute", top: 12, right: 16,
        background: "none", border: "none",
        fontSize: 18, cursor: "pointer",
        color: "var(--color-secondary)", padding: 0, margin: 0,
      }}
      title="Dismiss"
    >
      ×
    </button>

    <h3 style={{ margin: "0 0 4px", color: "var(--color-primary)" }}>
      {allDone ? "You're all set! 🎉" : "Getting started"}
    </h3>
    <p style={{ margin: "0 0 16px", fontSize: 14, color: "var(--color-secondary)" }}>
      {allDone
        ? "All steps complete. Your classroom is ready to go."
        : "Complete these steps to get your classroom up and running."}
    </p>

    <ChecklistItem
      done={steps.hasClasses}
      label="Create your first class"
      action={!steps.hasClasses && <Link to="/addclass"><button style={{ padding: "6px 14px", fontSize: 13 }}>Create Class</button></Link>}
    />
    <ChecklistItem
      done={steps.hasStudents}
      label="Add students to your class"
      locked={!steps.hasClasses}
      note={steps.hasClasses && !steps.hasStudents
        ? 'Click "Add Students" on your class below.'
        : null}
    />
    <ChecklistItem
      done={steps.hasVisitedSignOut}
      label="Open the sign-out screen and go"
      locked={!steps.hasStudents}
      action={steps.hasStudents && !steps.hasVisitedSignOut && (
        <Link to="/classsignoutpage"><button style={{ padding: "6px 14px", fontSize: 13 }}>Open Sign-Out Screen</button></Link>
      )}
      isLast
    />
  </div>
);

const ChecklistItem = ({ done, label, action, locked, note, isLast }) => (
  <div style={{
    display: "flex",
    gap: 12,
    paddingBottom: isLast ? 0 : 14,
    marginBottom: isLast ? 0 : 14,
    borderBottom: isLast ? "none" : "1px solid #f0f0f0",
    opacity: locked ? 0.4 : 1,
  }}>
    <div style={{
      width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: done ? "var(--color-primary)" : "#f0f0f0",
      border: done ? "none" : "2px solid #ddd",
      transition: "background 0.3s",
    }}>
      {done && (
        <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
          <path d="M1 4L4.5 7.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>

    <div style={{ flex: 1, paddingTop: 2 }}>
      <span style={{
        fontWeight: done ? 400 : 600,
        color: done ? "var(--color-secondary)" : "var(--color-text)",
        textDecoration: done ? "line-through" : "none",
        fontSize: 15,
      }}>
        {label}
      </span>
      {note && (
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-secondary)" }}>
          {note}
        </p>
      )}
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  </div>
);

export default Dashboard;
